import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with interceptor for token
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Add token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hikari_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export { api };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('hikari_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('hikari_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    const response = await api.post('/api/auth/login', { email, password, remember_me: rememberMe });
    if (response.data.access_token) {
      localStorage.setItem('hikari_token', response.data.access_token);
    }
    setUser(response.data.user || response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await api.post('/api/auth/register', { email, password, name });
    if (response.data.access_token) {
      localStorage.setItem('hikari_token', response.data.access_token);
    }
    setUser(response.data.user || response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('hikari_token');
    setUser(false);
  };

  const refreshToken = async () => {
    try {
      const response = await api.post('/api/auth/refresh');
      if (response.data.access_token) {
        localStorage.setItem('hikari_token', response.data.access_token);
      }
      await checkAuth();
    } catch (error) {
      localStorage.removeItem('hikari_token');
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshToken, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
